import {
  JOINED_STATUSES,
  NOT_JOINED_STATUSES,
  UK_JOINED_STATUSES,
  UK_NOT_JOINED_STATUSES,
} from './config.js';

// PER-BRAND CLUBWISE CONFIG. One small registry, keyed by the Kyck BRAND ACCOUNT id.
//
// ⚠️⚠️ THE BRAND KEY IS WRITTEN INTO EVERY BigQuery ROW (`brand`) AND SCOPES EVERY
// READ, REPLACE AND RECLASSIFY, together with the club code. Two brands may hand out
// the same club code, and without the brand in every predicate one club's upload
// would delete, read or report the other's.
// ⚠️ AU's key is `anytime_fitness_au` and MUST NOT CHANGE: every existing row carries it.
// ⚠️ An account's brand comes from its brand ANCESTRY (computeBrandAncestorMap), never
// from its name; `clubwiseBrandFor` takes that id.

export interface ClubwiseBrandRules {
  /** The value stored in `brand` on every row. */
  brandKey: string;
  /** The Kyck brand account id this entry belongs to. */
  brandAccountId: string;
  /** The exact `Account.country` value this brand's clubs carry (the AccountForm dropdown's spelling). */
  accountCountry: string;
  /** The country code the clubs view reports. */
  clubsCountryCode: string;
  currency: 'AUD' | 'GBP';
  /** The club-local zone: "today" for the data-up-to check is a club-local day. */
  timeZone: string;
  joinedStatuses: readonly string[];
  notJoinedStatuses: readonly string[];
  /**
   * ⚠️ UK ONLY. "Web joiner" says HOW someone joined (online), not where the lead
   * came from, so for a row whose Contact Type is `web joiner` the Contact Type is
   * IGNORED and the Source decides. AU's "Web joiner" keeps its Contact-Type meaning.
   */
  webJoinerBySource: boolean;
}

export const AF_AU_BRAND_ACCOUNT_ID = 'rBEU09v5W7cJ861X3GA3';
export const AF_UK_BRAND_ACCOUNT_ID = 'iUZTT9GkDYfHHEo3Q573';

export const AF_AU: ClubwiseBrandRules = {
  brandKey: 'anytime_fitness_au',
  brandAccountId: AF_AU_BRAND_ACCOUNT_ID,
  accountCountry: 'Australia',
  clubsCountryCode: 'AU',
  currency: 'AUD',
  timeZone: 'Australia/Melbourne',
  joinedStatuses: JOINED_STATUSES,
  notJoinedStatuses: NOT_JOINED_STATUSES,
  webJoinerBySource: false,
};

export const AF_UK: ClubwiseBrandRules = {
  brandKey: 'anytime_fitness_uk',
  brandAccountId: AF_UK_BRAND_ACCOUNT_ID,
  // ⚠️ What UK accounts actually store: the AccountForm country dropdown offers
  // 'United Kingdom' (not 'GB' or 'Great Britain').
  accountCountry: 'United Kingdom',
  clubsCountryCode: 'GB',
  currency: 'GBP',
  timeZone: 'Europe/London',
  joinedStatuses: UK_JOINED_STATUSES,
  notJoinedStatuses: UK_NOT_JOINED_STATUSES,
  webJoinerBySource: true,
};

export const CLUBWISE_BRANDS: Readonly<Record<string, ClubwiseBrandRules>> = {
  [AF_AU_BRAND_ACCOUNT_ID]: AF_AU,
  [AF_UK_BRAND_ACCOUNT_ID]: AF_UK,
};

/** The rules for a brand account id, or null (not a ClubWise brand). */
export function clubwiseBrandFor(brandAccountId: string | null | undefined): ClubwiseBrandRules | null {
  if (!brandAccountId) return null;
  return CLUBWISE_BRANDS[brandAccountId] ?? null;
}

/** The rules for a stored `brand` key, or null. */
export function clubwiseBrandByKey(brandKey: string | null | undefined): ClubwiseBrandRules | null {
  if (!brandKey) return null;
  return Object.values(CLUBWISE_BRANDS).find((b) => b.brandKey === brandKey) ?? null;
}

/**
 * The status vocabulary for a scope that may span brands: ONE brand's own lists, or
 * (a scope of both) the union. The shared words ('term at club', 'blow out', ...) mean
 * the same thing in both, so a union can never call a lost lead joined.
 */
export function mergedStatusRules(
  brands: readonly ClubwiseBrandRules[],
): Pick<ClubwiseBrandRules, 'joinedStatuses' | 'notJoinedStatuses'> {
  if (brands.length === 0) return AF_AU;
  return {
    joinedStatuses: [...new Set(brands.flatMap((b) => b.joinedStatuses))],
    notJoinedStatuses: [...new Set(brands.flatMap((b) => b.notJoinedStatuses))],
  };
}

/** Every registered brand key. */
export function allClubwiseBrandKeys(): string[] {
  return Object.values(CLUBWISE_BRANDS).map((b) => b.brandKey);
}
